import { useEffect, useMemo, useRef, useState } from "react";
import {
  Bath,
  CalendarDays,
  Car,
  ChevronLeft,
  ChevronRight,
  Dog,
  Heart,
  Home,
  Map,
  Menu,
  MessageCircle,
  PawPrint,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  Stethoscope,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import MarketplaceInlineSearchBar, {
  type MarketplaceInlineSearchValue,
} from "@/components/marketplace/MarketplaceInlineSearchBar";

type MarketplaceHost = {
  id: number;
  public_code?: string | null;
  name: string;
  city: string;
  state?: string;
  neighborhood?: string;
  cover_photo: string;
  photos: string[];
  profile_photo?: string | null;
  services: string[];
  starting_price: number;
  rating: number;
  review_count: number;
  verified: boolean;
  latitude?: number | null;
  longitude?: number | null;
  distance_km?: number | null;
  profile_url?: string | null;
};

type CityGroup = { city: string; state?: string; hosts: MarketplaceHost[] };
type MarketplacePayload = {
  nearby: MarketplaceHost[];
  cities: CityGroup[];
  meta?: {
    hosts?: number;
    cities?: number;
    nearby_mode?: "default" | "geolocation" | "city" | "unavailable";
    nearby_label?: string | null;
  };
};
type Category = { label: string; slug: string; icon: LucideIcon };

const LOCATION_STORAGE_KEY = "allepets:tutor-location";
const CITY_STORAGE_KEY = "allepets:last-city";
const LOCATION_MAX_AGE = 24 * 60 * 60 * 1000;

const categories: Category[] = [
  { label: "Hospedagem", slug: "hospedagem", icon: Home },
  { label: "Creche", slug: "creche", icon: PawPrint },
  { label: "Pet sitter", slug: "petsitter", icon: Heart },
  { label: "Passeio", slug: "passeio", icon: Dog },
  { label: "Táxi dog", slug: "taxi_dog", icon: Car },
  { label: "Banho e tosa", slug: "banho_e_tosa", icon: Bath },
  { label: "Adestramento", slug: "adestramento", icon: Sparkles },
  { label: "Veterinário em casa", slug: "veterinario_casa", icon: Stethoscope },
];

function normalizeCity(value?: string | null) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function readSavedLocation() {
  try {
    const raw = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as { latitude?: number; longitude?: number; savedAt?: number };
    if (!Number.isFinite(parsed.latitude) || !Number.isFinite(parsed.longitude)) return null;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > LOCATION_MAX_AGE) return null;

    return {
      latitude: Number(parsed.latitude),
      longitude: Number(parsed.longitude),
    };
  } catch {
    return null;
  }
}

function saveLocation(latitude: number, longitude: number) {
  try {
    localStorage.setItem(
      LOCATION_STORAGE_KEY,
      JSON.stringify({ latitude, longitude, savedAt: Date.now() }),
    );
  } catch {
    // A vitrine continua funcionando sem persistência local.
  }
}

function HostCard({ host }: { host: MarketplaceHost }) {
  const [favorite, setFavorite] = useState(false);
  const href = host.profile_url || `/buscar?anfitriao=${host.id}`;

  return (
    <article className="w-[74vw] max-w-[294px] shrink-0 snap-start md:w-[294px]">
      <div className="group relative aspect-[4/3] overflow-hidden rounded-[24px] bg-slate-100">
        <a href={href} className="block h-full w-full">
          <img
            src={host.cover_photo}
            alt={`Espaço de ${host.name}`}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
        </a>
        <button
          type="button"
          aria-label={favorite ? "Remover dos favoritos" : "Favoritar anfitrião"}
          aria-pressed={favorite}
          onClick={() => setFavorite((value) => !value)}
          className="absolute right-3 top-3 grid h-10 w-10 place-items-center rounded-full bg-white/90 shadow-sm backdrop-blur"
        >
          <Heart className={`h-5 w-5 ${favorite ? "fill-[#FE5668] text-[#FE5668]" : "text-slate-800"}`} />
        </button>
        {host.verified && (
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-800">
            <ShieldCheck className="h-4 w-4 text-[#64A002]" /> Verificado
          </span>
        )}
      </div>
      <a href={href} className="mt-3 block space-y-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate font-bold text-slate-950">{host.name}</h3>
            <p className="truncate text-sm text-slate-500">{[host.neighborhood, host.city].filter(Boolean).join(", ")}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1 text-sm">
            <Star className="h-4 w-4 fill-slate-900" />
            {host.rating > 0 ? host.rating.toFixed(1) : "Novo"}
          </div>
        </div>
        <p className="truncate text-sm text-slate-500">{host.services.slice(0, 3).join(" · ")}</p>
        {Number.isFinite(host.distance_km) && (
          <p className="text-xs font-semibold text-[#64A002]">
            A aproximadamente {host.distance_km?.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} km
          </p>
        )}
        <p className="text-sm text-slate-700">
          <strong className="text-base text-slate-950">R$ {Math.round(host.starting_price)}</strong> a partir de
        </p>
      </a>
    </article>
  );
}

function HostSection({ title, hosts }: { title: string; hosts: MarketplaceHost[] }) {
  const carouselRef = useRef<HTMLDivElement | null>(null);

  if (!hosts.length) return null;

  const move = (direction: -1 | 1) => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    carousel.scrollBy({
      left: direction * Math.max(310, carousel.clientWidth * 0.8),
      behavior: "smooth",
    });
  };

  return (
    <section className="py-5 md:py-8">
      <div className="mb-4 flex items-center justify-between gap-4 px-4 md:px-0">
        <h2 className="text-xl font-black tracking-tight text-slate-950 md:text-2xl">{title}</h2>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => move(-1)}
            className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 active:scale-95"
            aria-label={`Voltar cartões de ${title}`}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => move(1)}
            className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 active:scale-95"
            aria-label={`Avançar cartões de ${title}`}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div
        ref={carouselRef}
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-4 pb-3 scrollbar-none md:px-0"
      >
        {hosts.map((host) => <HostCard key={`${title}-${host.id}`} host={host} />)}
      </div>
    </section>
  );
}

export default function MarketplaceHome() {
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  const navigate = useNavigate();
  const [data, setData] = useState<MarketplacePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<MarketplaceInlineSearchValue>({
    service: "",
    city: "",
    startDate: "",
    endDate: "",
    petCount: "1",
  });

  useEffect(() => {
    let cancelled = false;

    const fetchHome = (params?: URLSearchParams) => {
      const suffix = params && params.size > 0 ? `?${params.toString()}` : "";

      return fetch(`/api/marketplace/home${suffix}`, { headers: { Accept: "application/json" } })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((payload) => {
          if (!cancelled) {
            setData(payload);
            setError(null);
          }
        });
    };

    const savedLocation = readSavedLocation();
    const savedCity = localStorage.getItem(CITY_STORAGE_KEY) || "";

    if (savedLocation) {
      const params = new URLSearchParams({
        latitude: String(savedLocation.latitude),
        longitude: String(savedLocation.longitude),
      });
      fetchHome(params).catch((reason) => !cancelled && setError(reason.message));
    } else if (savedCity) {
      const params = new URLSearchParams({ cidade: savedCity });
      fetchHome(params).catch((reason) => !cancelled && setError(reason.message));
    } else {
      fetchHome().catch((reason) => !cancelled && setError(reason.message));
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          if (cancelled) return;

          const latitude = Number(coords.latitude.toFixed(4));
          const longitude = Number(coords.longitude.toFixed(4));
          saveLocation(latitude, longitude);

          const params = new URLSearchParams({
            latitude: String(latitude),
            longitude: String(longitude),
          });

          fetchHome(params).catch(() => undefined);
        },
        () => undefined,
        { enableHighAccuracy: false, timeout: 6000, maximumAge: 3600000 },
      );
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const availableCities = useMemo(
    () => Array.from(new Set(data?.cities.map((group) => group.city).filter(Boolean) || [])).sort((a, b) => a.localeCompare(b, "pt-BR")),
    [data]
  );

  const nearbyTitle = useMemo(() => {
    if (data?.meta?.nearby_label && data.nearby.length > 0) {
      return `Anfitriões próximos em ${data.meta.nearby_label}`;
    }

    return "Anfitriões próximos";
  }, [data]);

  const cityGroups = useMemo(() => {
    const groups = data?.cities || [];
    const nearbyCity = data?.nearby.length ? normalizeCity(data.meta?.nearby_label) : "";

    if (!nearbyCity) return groups;

    return groups.filter((group) => normalizeCity(group.city) !== nearbyCity);
  }, [data]);

  const submitSearch = (value: MarketplaceInlineSearchValue, view?: "map") => {
    setSearchValue(value);

    if (value.city) {
      try {
        localStorage.setItem(CITY_STORAGE_KEY, value.city.split(",")[0].trim());
      } catch {
        // A busca segue normalmente sem persistência local.
      }
    }

    const params = new URLSearchParams();
    if (value.service) params.set("service", value.service);
    if (value.city) params.set("cidade", value.city);
    if (value.startDate) params.set("entrada", value.startDate);
    if (value.endDate) params.set("saida", value.endDate);
    params.set("quantidade_pets", value.petCount || "1");
    if (view) params.set("view", view);
    navigate(`/buscar?${params.toString()}`);
  };

  const openAuthModal = () => {
    setAccountMenuOpen(false);

    window.setTimeout(() => {
      const modalElement = document.getElementById("authModal");
      const bootstrapApi = (window as typeof window & {
        bootstrap?: {
          Modal?: {
            getOrCreateInstance: (
              element: HTMLElement,
              options?: Record<string, unknown>,
            ) => { show: () => void };
          };
        };
      }).bootstrap;

      if (!modalElement || !bootstrapApi?.Modal) {
        console.error("Modal de autenticação indisponível.");
        return;
      }

      bootstrapApi.Modal.getOrCreateInstance(modalElement, {
        backdrop: true,
        keyboard: true,
        focus: false,
      }).show();
    }, 0);
  };

  return (
    <div className="min-h-screen bg-[#fffdfa] pb-24 text-slate-950 md:pb-0">
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:h-20 md:px-6">
          <a href="/" className="allepets-brand text-3xl leading-none" aria-label="AllePets - início">
            <span className="text-[#FE5668]">Alle</span><span className="text-[#64A002]">Pets</span>
          </a>
          <nav className="hidden items-center gap-7 text-sm font-semibold text-slate-700 lg:flex">
            <a href="/">Explorar</a>
            <a href="/como-funciona">Como funciona</a>
            <a href="/anfitrioes">Para anfitriões</a>
            <a href="/ajuda">Ajuda</a>
          </nav>
          <div className="relative flex items-center gap-2">
            <a href="/comecar" className="hidden rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-100 sm:block">Quero ser anfitrião</a>
            <button
              type="button"
              aria-label={accountMenuOpen ? "Fechar menu" : "Abrir menu"}
              aria-haspopup="menu"
              aria-expanded={accountMenuOpen}
              onClick={() => setAccountMenuOpen((open) => !open)}
              className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 shadow-sm transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#64A002] focus-visible:ring-offset-2"
            >
              <Menu className="h-5 w-5" /><UserRound className="h-5 w-5" />
            </button>
            {accountMenuOpen && (
              <div
                role="menu"
                aria-label="Menu da conta"
                className="absolute right-0 top-[calc(100%+0.65rem)] z-50 w-56 overflow-hidden rounded-2xl border border-slate-200 bg-white py-2 shadow-xl"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={openAuthModal}
                  className="block w-full px-4 py-3 text-left text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  Entrar
                </button>
                <a role="menuitem" href="/register" className="block px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50">Criar conta</a>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl md:px-6">
        <section className="px-4 pb-4 pt-5 md:px-0 md:pb-8 md:pt-10">
          <div className="grid items-center gap-8 lg:grid-cols-[0.95fr_1.05fr]">
            <div>
              <p className="mb-2 text-sm font-bold uppercase tracking-[0.18em] text-[#64A002]">Cuidado perto de você</p>
              <h1 className="max-w-2xl text-3xl font-black leading-tight tracking-tight md:text-5xl">Cuidado, amor e confiança para o seu pet.</h1>
              <p className="mt-4 max-w-xl text-base leading-7 text-slate-600">Encontre anfitriões e profissionais pet verificados, com fotos reais, preços claros e atendimento perto de você.</p>
            </div>
            <div className="hidden min-h-[280px] overflow-hidden rounded-[32px] bg-gradient-to-br from-[#FEC1A5]/55 via-white to-[#B9D394]/45 p-8 lg:flex lg:items-center lg:justify-center">
              <div className="max-w-md text-center">
                <div className="mx-auto flex items-center justify-center gap-4" aria-label="Ícones AllePets">
                  <img src="/images/allepets/coracao.png" alt="Coração AllePets" className="h-16 w-16 object-contain mix-blend-multiply" />
                  <img src="/images/allepets/bola.png" alt="Bola AllePets" className="h-16 w-16 object-contain mix-blend-multiply" />
                  <img src="/images/allepets/casa.png" alt="Casa AllePets" className="h-16 w-16 object-contain mix-blend-multiply" />
                </div>
                <p className="mt-5 text-2xl font-black">Conecta amor, cuida bem.</p>
                <p className="mt-2 text-sm leading-6 text-slate-600">Uma experiência simples para encontrar o cuidado certo e reservar com segurança.</p>
              </div>
            </div>
          </div>

          <MarketplaceInlineSearchBar
            value={searchValue}
            cities={availableCities}
            onChange={setSearchValue}
            onSubmit={submitSearch}
            className="relative z-30 mx-auto mt-7 max-w-5xl"
          />
        </section>

        <section className="border-y border-slate-100 bg-white py-4 md:rounded-[28px] md:border">
          <div className="flex gap-3 overflow-x-auto px-4 scrollbar-none md:grid md:grid-cols-8 md:px-5">
            {categories.map(({ label, slug, icon: Icon }, index) => (
              <button
                key={slug}
                type="button"
                onClick={() => {
                  setSearchValue((current) => ({ ...current, service: slug }));
                }}
                className="flex min-w-[88px] flex-col items-center gap-2 rounded-2xl px-3 py-3 text-center text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                <span className={`grid h-11 w-11 place-items-center rounded-2xl ${index % 2 === 0 ? "bg-[#FE5668]/10 text-[#FE5668]" : "bg-[#B9D394]/30 text-[#64A002]"}`}>
                  <Icon className="h-6 w-6" />
                </span>
                {label}
              </button>
            ))}
          </div>
        </section>

        {error && <div className="mx-4 my-6 rounded-2xl bg-red-50 p-4 text-sm text-red-700 md:mx-0">Não foi possível carregar os anfitriões: {error}</div>}
        {!data && !error && <div className="px-4 py-10 text-sm text-slate-500 md:px-0">Carregando anfitriões…</div>}
        {data && (
          <>
            <HostSection title={nearbyTitle} hosts={data.nearby} />
            {cityGroups.map((group) => (
              <HostSection key={`${group.city}-${group.state}`} title={`Anfitriões em ${group.city}`} hosts={group.hosts} />
            ))}
          </>
        )}
      </main>

      <button
        type="button"
        onClick={() => submitSearch(searchValue, "map")}
        className="fixed bottom-24 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-white shadow-xl md:bottom-8"
      >
        <Map className="h-4 w-4" /> Mostrar mapa
      </button>

      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-slate-200 bg-white px-2 pb-[max(env(safe-area-inset-bottom),8px)] pt-2 md:hidden">
        {[[Search, "Explorar", "/"], [Heart, "Favoritos", "/favoritos"], [CalendarDays, "Reservas", "/tutor/reservas"], [MessageCircle, "Mensagens", "/chat"], [UserRound, "Perfil", "/home"]].map(([Icon, label, href]: any) => (
          <a key={label} href={href} className={`flex flex-col items-center gap-1 py-1 text-[11px] font-medium ${label === "Explorar" ? "text-[#FE5668]" : "text-slate-500"}`}>
            <Icon className="h-5 w-5" />{label}
          </a>
        ))}
      </nav>
    </div>
  );
}
